import React, { useState, useEffect, useRef } from 'react';
import {
  RemediationPolicy,
  RemediationMode,
  getRemediationPolicy,
  updateRemediationPolicy,
} from '../api';
import { Callout, Checkbox, Dialog, Input, OptionCard, OptionGroup, Subsection } from './primitives';
import { Field, Skeleton } from './ui';

interface RemediationPolicyModalProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: (policy: RemediationPolicy) => void;
}

const modes: { value: RemediationMode; title: string; description: string }[] = [
  { value: 'observe_only', title: 'Observe only', description: 'Suggests fixes and never changes anything.' },
  { value: 'approval_required', title: 'Approval required', description: 'A person approves every fix before it runs.' },
  { value: 'auto_safe', title: 'Automatic for safe fixes', description: 'Would run verified low-risk fixes on its own.' },
];

/** Nothing proposes or runs fixes on its own yet (RES-081). */
const automaticAvailable = false;

export const RemediationPolicyModal: React.FC<RemediationPolicyModalProps> = ({
  projectId,
  isOpen,
  onClose,
  onSaved,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<{ title: string; text: string } | null>(null);
  const saveRef = useRef<HTMLButtonElement>(null);
  const [refocusSave, setRefocusSave] = useState(false);

  const [mode, setMode] = useState<RemediationMode>('approval_required');
  const [wasAutomatic, setWasAutomatic] = useState(false);
  const [autoRestartConsumers, setAutoRestartConsumers] = useState(true);
  const [autoRestartApp, setAutoRestartApp] = useState(false);
  const [autoRollbackDeployment, setAutoRollbackDeployment] = useState(false);
  const [autoRetryBackup, setAutoRetryBackup] = useState(true);
  const [autoCleanCache, setAutoCleanCache] = useState(false);
  const [maxAttempts, setMaxAttempts] = useState(2);
  const [cooldownMinutes, setCooldownMinutes] = useState(10);
  const [circuitBreakerWindowMin, setCircuitBreakerWindowMin] = useState(30);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    getRemediationPolicy(projectId)
      .then((res) => {
        const p = res.policy;
        // A project once set to automatic fixes: they never ran, and the
        // mode cannot be saved; saving moves it to approval.
        setWasAutomatic(p.mode === 'auto_safe' && !automaticAvailable);
        setMode(p.mode === 'auto_safe' && !automaticAvailable ? 'approval_required' : p.mode);
        setAutoRestartConsumers(p.auto_restart_consumers);
        setAutoRestartApp(p.auto_restart_app);
        setAutoRollbackDeployment(p.auto_rollback_deployment);
        setAutoRetryBackup(p.auto_retry_backup);
        setAutoCleanCache(p.auto_clean_cache);
        setMaxAttempts(p.max_attempts || 2);
        setCooldownMinutes(p.cooldown_minutes || 10);
        setCircuitBreakerWindowMin(p.circuit_breaker_window_min || 30);
      })
      .catch((err: any) => {
        setError({ title: 'The policy could not be loaded', text: err?.message || 'Failed to load policy' });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isOpen, projectId]);

  // The Save button is disabled while saving, which drops focus on the page
  // behind the dialog: after a failed save, put it back once it is enabled.
  useEffect(() => {
    if (!refocusSave || saving) return;
    saveRef.current?.focus();
    setRefocusSave(false);
  }, [refocusSave, saving]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const res = await updateRemediationPolicy(projectId, {
        mode,
        auto_restart_consumers: autoRestartConsumers,
        auto_restart_app: autoRestartApp,
        auto_rollback_deployment: autoRollbackDeployment,
        auto_retry_backup: autoRetryBackup,
        auto_clean_cache: autoCleanCache,
        max_attempts: Number(maxAttempts),
        cooldown_minutes: Number(cooldownMinutes),
        circuit_breaker_window_min: Number(circuitBreakerWindowMin),
      });
      onSaved?.(res.policy);
      onClose();
    } catch (err: any) {
      setError({ title: 'The policy was not saved', text: err?.message || 'Failed to save policy' });
      setRefocusSave(true);
    } finally {
      setSaving(false);
    }
  };

  const toggles: { key: string; title: string; description: string; checked: boolean; onChange: (value: boolean) => void }[] = [
    {
      key: 'consumers',
      title: 'Restart stalled queue consumers',
      description: 'Restarts Magento queue consumers that stopped processing messages.',
      checked: autoRestartConsumers,
      onChange: setAutoRestartConsumers,
    },
    {
      key: 'cache',
      title: 'Clean the Magento cache',
      description: 'Runs bin/magento cache:clean when the storefront starts failing HTTP checks.',
      checked: autoCleanCache,
      onChange: setAutoCleanCache,
    },
    {
      key: 'backup',
      title: 'Retry failed backups',
      description: 'Retries database or media backups that timed out.',
      checked: autoRetryBackup,
      onChange: setAutoRetryBackup,
    },
    {
      key: 'app',
      title: 'Restart crashed app services',
      description: 'Restarts php-fpm or web containers that stopped.',
      checked: autoRestartApp,
      onChange: setAutoRestartApp,
    },
    {
      key: 'rollback',
      title: 'Roll back a deployment that caused an incident',
      description: 'Only when the deployment’s database changes are backward-compatible.',
      checked: autoRollbackDeployment,
      onChange: setAutoRollbackDeployment,
    },
  ];

  return (
    <Dialog
      open
      size="lg"
      title="Fix policy"
      onClose={onClose}
      busy={saving}
      onSubmit={handleSubmit}
      testId="remediation-policy-dialog"
      footer={
        <>
          <button type="button" className="button button--secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button ref={saveRef} type="submit" className="button button--primary" disabled={saving || loading}>
            {saving ? 'Saving' : 'Save policy'}
          </button>
        </>
      }
    >
      <Callout tone="info" role="note" title="Fixes run only when a person approves them">
        Commerce Ops does not run any fix on its own yet. The automatic options below are kept for when that arrives.
      </Callout>
      {wasAutomatic ? (
        <Callout tone="warning" role="note" title="This project was set to automatic fixes">
          They never ran. Saving the policy sets it to Approval required.
        </Callout>
      ) : null}
      {error ? (
        <Callout tone="danger" title={error.title}>
          {error.text}
        </Callout>
      ) : null}

      {loading ? (
        <div className="policy-skeleton" role="status" aria-label="Loading the policy">
          <Skeleton width="80px" height="18px" />
          <div className="policy-skeleton__row">
            {modes.map((m) => (
              <Skeleton key={m.value} width="100%" height="92px" radius="var(--radius-lg)" />
            ))}
          </div>
          <Skeleton width="220px" height="18px" />
          {toggles.map((t) => (
            <Skeleton key={t.key} width="100%" height="40px" />
          ))}
          <Skeleton width="80px" height="18px" />
          <div className="policy-skeleton__row">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} width="100%" height="56px" />
            ))}
          </div>
        </div>
      ) : (
        <>
          <Subsection title="Mode">
            <div className="policy-modes">
              <OptionGroup label="Mode">
              {modes.map((m) => (
                <OptionCard
                  key={m.value}
                  title={m.title}
                  description={m.description}
                  selected={mode === m.value}
                  selectedLabel="Selected"
                  disabled={m.value === 'auto_safe' && !automaticAvailable}
                  badge={m.value === 'auto_safe' && !automaticAvailable ? 'Not available yet' : undefined}
                  onSelect={() => setMode(m.value)}
                  testId={`remediation-mode-${m.value}`}
                />
              ))}
              </OptionGroup>
            </div>
          </Subsection>

          <Subsection title="Fixes it may run on its own" description="For automatic fixes, which are not available yet.">
            <ul className="policy-toggles">
              {toggles.map((t) => (
                <li key={t.key}>
                  <Checkbox
                    checked={t.checked}
                    onChange={(e) => t.onChange(e.target.checked)}
                    label={
                      <>
                        <span className="policy-toggle__text">
                          <span className="policy-toggle__title">{t.title}</span>
                          <span className="policy-toggle__desc">{t.description}</span>
                        </span>
                      </>
                    }
                  />
                </li>
              ))}
            </ul>
          </Subsection>

          <Subsection title="Limits" description="Attempts are counted per problem; when they run out, the incident is escalated to a person.">
            <div className="policy-limits">
              <Field label="Maximum attempts" hint="1 to 5, within the window.">
                <Input type="number" min={1} max={5} value={maxAttempts} onChange={(e) => setMaxAttempts(Number(e.target.value))} />
              </Field>
              <Field label="Wait between attempts (minutes)" hint="1 to 120.">
                <Input type="number" min={1} max={120} value={cooldownMinutes} onChange={(e) => setCooldownMinutes(Number(e.target.value))} />
              </Field>
              <Field label="Circuit breaker window (minutes)" hint="5 to 240.">
                <Input
                  type="number"
                  min={5}
                  max={240}
                  value={circuitBreakerWindowMin}
                  onChange={(e) => setCircuitBreakerWindowMin(Number(e.target.value))}
                />
              </Field>
            </div>
          </Subsection>
        </>
      )}
    </Dialog>
  );
};
